/** @internal – import from `@/store/useDepartmentStore` instead. */
import { getAccessToken } from "@/lib/jwt-auth-client";
import { getPipelineMaxStepIndex } from "@/config/coAgentSteps";
import type { CoAgentType, StepStatus, StepLog, ThoughtEvent, CampaignData } from "./types";
import {
  PIPELINE_STEPS,
  SOCIAL_STEPS,
  BRAND_STEPS,
  GROWTH_STEPS,
  GEO_STEPS,
  ECOMMERCE_STEPS,
} from "./types";

export const generateId = () => Math.random().toString(36).substring(2, 9);

export const DEFAULT_STEP_STATUSES: Record<number, StepStatus> = Object.fromEntries(
  PIPELINE_STEPS.map((_, i) => [i, "pending" as StepStatus]),
);

export const DEFAULT_STEP_LOGS: Record<number, StepLog[]> = Object.fromEntries(
  PIPELINE_STEPS.map((_, i) => [i, []]),
);

export const DEFAULT_THOUGHT_EVENTS: Record<number, ThoughtEvent[]> = Object.fromEntries(
  PIPELINE_STEPS.map((_, i) => [i, []]),
);

let _logIdCounter = 0;
export const nextLogId = () => ++_logIdCounter;
export const nowTs = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

/** Build headers with JWT auth token for API calls. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

type ApiErrorDetailEntry = {
  path?: string;
  message?: string;
};

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function formatErrorDetails(details: unknown): string {
  if (Array.isArray(details)) {
    return (details as ApiErrorDetailEntry[])
      .map((d) => `${d.path || "request"}: ${d.message || "invalid value"}`)
      .join("; ");
  }

  if (!details || typeof details !== "object") return "";

  const payload = details as Record<string, unknown>;
  const parts: string[] = [];

  const source =
    (typeof payload.context === "string" && payload.context) ||
    (typeof payload.detail === "string" && payload.detail) ||
    (typeof payload.source === "string" && payload.source) ||
    "";
  if (source) parts.push(`source=${source}`);

  const needed = toFiniteNumber(payload.needed);
  if (needed !== null) parts.push(`needed=${needed}`);

  const remaining = toFiniteNumber(payload.remaining);
  if (remaining !== null) parts.push(`remaining=${remaining}`);

  const cost = toFiniteNumber(payload.cost);
  if (cost !== null) parts.push(`cost=${cost}`);

  const creditsTotal = toFiniteNumber(payload.creditsTotal);
  if (creditsTotal !== null) parts.push(`total=${creditsTotal}`);

  const creditsUsed = toFiniteNumber(payload.creditsUsed);
  if (creditsUsed !== null) parts.push(`used=${creditsUsed}`);

  if (typeof payload.category === "string" && payload.category) {
    parts.push(`category=${payload.category}`);
  }

  if (parts.length > 0) return parts.join(", ");

  return Object.entries(payload)
    .slice(0, 6)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(", ");
}

export function normalizeErrorBase(base: unknown, fallback: string): string {
  if (typeof base === "string") {
    const trimmed = base.trim();
    return trimmed || fallback;
  }

  if (base instanceof Error) {
    const msg = base.message?.trim();
    return msg || fallback;
  }

  if (base && typeof base === "object") {
    const candidate = (base as Record<string, unknown>).message;
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    try {
      const serialized = JSON.stringify(base);
      if (serialized && serialized !== "{}" && serialized !== "[]") return serialized;
    } catch {
      // ignore serialization errors and fall through to fallback
    }
  }

  return fallback;
}

export function buildErrorMessage(
  base: unknown,
  details: unknown,
  fallback = "Pipeline failed",
): string {
  const normalizedBase = normalizeErrorBase(base, fallback);
  const detailText = formatErrorDetails(details);
  return detailText ? `${normalizedBase} (${detailText})` : normalizedBase;
}

export function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function getMaxStepIndexForType(coAgentType: CoAgentType = "ads"): number {
  return getPipelineMaxStepIndex(coAgentType);
}

export function getStepsForType(coAgentType: CoAgentType = "ads"): string[] {
  if (coAgentType === "products") return ECOMMERCE_STEPS;
  if (coAgentType === "geo") return GEO_STEPS;
  if (coAgentType === "ads") return GROWTH_STEPS;
  if (coAgentType === "brand-dna") return BRAND_STEPS;
  return PIPELINE_STEPS;
}

export function getStepLabel(stepIndex: number, coAgentType: CoAgentType = "ads"): string {
  const steps = getStepsForType(coAgentType);
  return steps[stepIndex] ?? `Step ${stepIndex + 1}`;
}

export function appendStepLogEntry(
  stepLogs: Record<number, StepLog[]>,
  stepIndex: number,
  text: string,
): Record<number, StepLog[]> {
  return {
    ...stepLogs,
    [stepIndex]: [...(stepLogs[stepIndex] ?? []), { id: nextLogId(), text, ts: nowTs() }],
  };
}

export function normalizeStepIndex(index: number, coAgentType: CoAgentType = "ads"): number {
  const lastStepIndex = getMaxStepIndexForType(coAgentType);
  return Math.max(0, Math.min(lastStepIndex, index));
}

export function clearActiveStepStatuses(
  stepStatuses: Record<number, StepStatus>,
  maxStepIndex: number,
): Record<number, StepStatus> {
  const next = { ...stepStatuses };
  for (let i = 0; i <= maxStepIndex; i += 1) {
    if (next[i] === "running" || next[i] === "waiting") {
      next[i] = "pending";
    }
  }
  return next;
}

export function getAdvancedStepState(
  currentStepIndex: number,
  stepStatuses: Record<number, StepStatus>,
  nextStatus: "running" | "waiting" = "running",
  coAgentType: CoAgentType = "ads",
): { currentStepIndex: number; stepStatuses: Record<number, StepStatus> } {
  const lastStepIndex = getMaxStepIndexForType(coAgentType);
  const current = normalizeStepIndex(currentStepIndex, coAgentType);
  const next = Math.min(current + 1, lastStepIndex);
  const statuses = clearActiveStepStatuses(stepStatuses, lastStepIndex);
  statuses[current] = "completed";

  if (next > current) {
    statuses[next] = nextStatus;
  }

  return {
    currentStepIndex: next,
    stepStatuses: statuses,
  };
}

// ── Product metric helpers (re-exported from shared lib) ──────────────────
export { recalcDerivedMetrics, mergeProductMetrics } from "@/features/products/lib/product-metrics";

export function mapResearchDoneData(data: Record<string, any>): Partial<CampaignData> {
  const {
    geo_plan,
    ads_plan,
    content_plan,
    geo_matrix,
    total_credits_used,
    query_plan,
    collection_stats,
    collection_errors,
    competitors,
    value_props,
  } = data;

  return {
    researchStatus: "complete",
    queryPlan: query_plan,
    geoMatrix: geo_matrix,
    geoPlan: geo_plan,
    adsPlan: ads_plan,
    contentPlan: content_plan,
    competitors,
    valueProps: value_props,
    collectionReport: {
      stats: collection_stats,
      errors: collection_errors,
      geo_matrix,
    },
    totalCreditsUsed: total_credits_used,
  };
}

export function mapSocialResearchDoneData(data: Record<string, any>): Partial<CampaignData> {
  return {
    socialResearchStatus: "complete",
    trendReport: data.trend_report,
    competitorAudit: data.competitor_audit,
    templateLibrary: data.template_library,
    socialCalendar: data.social_calendar,
    videoBriefs: data.video_briefs,
    engagementBenchmarks: data.engagement_benchmarks,
    totalCreditsUsed: data.total_credits_used,
  };
}
